import React, { useState, useEffect } from 'react';
import { DateTime } from 'luxon';

import { useClassName } from 'common/hooks';
import { optimizeContentfulImageUrl } from 'common/components';

import HomepageAsset from 'public/screens/Home/Components/Asset';
import Actions from '../Actions';
import useCustomStyle from '../../hooks';
import { TIME_ZONE } from 'public/screens/Cart/helpers/discounts';
import OperationalHoursModal from 'public/screens/CustomLandingPage/Components/OperationalHoursModal/OperationalHoursModal';
import { getContentRides } from 'public/components/AttractionScreen/RideClosures';
import { CTA_TYPES, STANDARD_EVENTS } from 'utils/analytics/trackV2';

import './card-s.less';
import Link from 'public/screens/CustomLandingPage/Components/Link';

export default function CardTypeS({ item, venue, refs, handleTrack }) {
  const classNames = useClassName('CLPCardTypeS');
  const [setRef, style] = useCustomStyle(item.fields.settings);
  const title = item.fields.title;
  const subtitle = item.fields.subtitle;

  const localDate = DateTime.local();
  const [date, setDate] = useState(localDate);
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [openHoursModal, setOpenHoursModal] = useState(false);

  useEffect(() => {
    if (!venue) return;
    const unavailableDates = venue?.priceTypes?.reduce(
      (acc, { date, type }) => (type === 'closed' ? acc.concat(date) : acc),
      []
    );
    setUnavailableDates(unavailableDates);
  }, [venue]);

  const imageStyles = Object.fromEntries(
    Object.entries(style)
      .filter(([key]) => key.startsWith('--asset-'))
      .map(([key, value]) => [key.replace('--asset-', ''), value])
  );
  const ctaStyles = Object.fromEntries(
    Object.entries(style)
      .filter(([key]) => key.startsWith('--cta-'))
      .map(([key, value]) => [key.replace('--cta-', ''), value])
  );
  const containerStyles = Object.fromEntries(
    Object.entries(style)
      .filter(([key]) => key.startsWith('--container-'))
      .map(([key, value]) => [key.replace('--container-', ''), value])
  );
  const infoStyles = Object.fromEntries(
    Object.entries(style)
      .filter(([key]) => key.startsWith('--info-'))
      .map(([key, value]) => [key.replace('--info-', ''), value])
  );

  const getHours = () => {
    const openingHours = venue.openingHours;
    if (!openingHours?.regular?.length && !openingHours?.irregular?.length)
      return null;

    return (
      openingHours.irregular.find(
        (irregular) => irregular.date === localDate.toFormat('yyyy-MM-dd')
      ) ||
      openingHours.regular.find(
        (hours) => hours.weekday === localDate.toFormat('cccc').toLowerCase()
      )
    );
  };

  const getDateTime = (closedAllDay, temporarilyClosed, hours, minutes) => {
    return (
      !closedAllDay &&
      !temporarilyClosed &&
      DateTime.fromISO(localDate.toFormat('yyyy-MM-dd'))
        .setZone(TIME_ZONE)
        .set({ hour: hours, minute: minutes })
    );
  };

  const getTimeFromDate = (date) => (date ? date.toFormat('h:mm a') : '');

  const getLabel = () => {
    if (!venue) return '';
    const openingHours = venue.openingHours;
    const comingSoon = venue?.featuredFlags?.includes('Coming Soon');

    if (comingSoon) return 'Coming Soon';

    const operationHours = openingHours && getHours();
    const temporarilyClosed = openingHours
      ? openingHours.temporarilyClosed
      : false;
    const closedAllDay = operationHours ? operationHours.isClosed : true;

    if (closedAllDay || temporarilyClosed) return 'Closed';

    const startTime = getDateTime(
      closedAllDay,
      temporarilyClosed,
      operationHours?.openHour,
      operationHours?.openMinute
    );
    const endTime = getDateTime(
      closedAllDay,
      temporarilyClosed,
      operationHours?.closeHour,
      operationHours?.closeMinute
    );

    if (!startTime || !endTime) return 'Closed';

    return `${getTimeFromDate(startTime)} - ${getTimeFromDate(endTime)}`;
  };

  const isRegularHoursClosed = (isoDate) => {
    return venue?.openingHours?.regular.some((regular) => {
      if (!regular.isClosed) return false;

      return (
        regular.weekday === DateTime.fromISO(isoDate).weekdayLong.toLowerCase()
      );
    });
  };

  const isIrregularHoursClosed = (isoDate) => {
    const irregularExists = Boolean(
      venue?.openingHours?.irregular.find(
        (irregular) => irregular?.date === isoDate
      )
    );

    if (!irregularExists) return true;

    return venue?.openingHours?.irregular.some((irregular) => {
      if (!irregular.isClosed) return false;

      if (irregular.type === 'day') {
        return irregular.date === isoDate;
      }

      const date = DateTime.fromISO(isoDate);
      if (irregular.type === 'range') {
        const start = DateTime.fromISO(irregular.startDate);
        const end = DateTime.fromISO(irregular.endDate);
        return start <= date && end >= date;
      }

      if (irregular.type === 'holiday') {
        return (
          irregular.holidayMonth + 1 === date.month &&
          irregular.holidayDate === date.day
        );
      }
    });
  };

  const isPromoClosed = (isoDate) => {
    const date = DateTime.fromISO(isoDate);
    if (venue?.startDate && date < DateTime.fromISO(venue.startDate))
      return true;

    return venue?.endDate && date > DateTime.fromISO(venue.endDate);
  };

  const isDateUnavailable = (isoDate) => {
    return (
      (isRegularHoursClosed(isoDate) && isIrregularHoursClosed(isoDate)) ||
      unavailableDates?.includes(isoDate) ||
      isPromoClosed(isoDate)
    );
  };
  const externalLink = item.fields.links?.find(
    (link) => link.fields.type === 'external'
  );
  const logoOverlayFields = item.fields.logoOverlay?.fields;

  const time = getLabel();
  const ridesCards = venue ? getContentRides(venue) : [];
  const openingHoursBanner = logoOverlayFields && {
    name: logoOverlayFields.name,
    asset: {
      mobile: {
        assetUrl: optimizeContentfulImageUrl(
          logoOverlayFields.file.url,
          logoOverlayFields.file.contentType
        ),
      },
    },
  };

  const handleExternalLinkClick = () => {
    if (handleTrack) {
      handleTrack({
        event: STANDARD_EVENTS.click,
        params: {
          cta_type: CTA_TYPES.link,
          destination_url: externalLink.fields.url,
        },
      });
    }
  };

  return (
    <div
      className={classNames('container')}
      ref={setRef}
      style={containerStyles}>
      {item.fields.asset && (
        <HomepageAsset
          asset={item.fields.asset}
          className={classNames('image')}
          style={imageStyles}
        />
      )}
      <div className={classNames('info')} style={infoStyles}>
        <div className={classNames('info-inner')}>
          <div className={classNames('info-title')}>{title}</div>
          <div className={classNames('info-time')}>{time}</div>
        </div>
        {externalLink ? (
          <a
            href={externalLink.fields.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleExternalLinkClick}
            className={classNames('info-subtitle')}>
            {subtitle}
          </a>
        ) : (
          venue &&
          openingHoursBanner && (
            <OperationalHoursModal
              ridesCards={ridesCards}
              isDateUnavailable={isDateUnavailable}
              open={openHoursModal}
              trigger={
                <a
                  onClick={() => setOpenHoursModal(true)}
                  className={classNames('info-subtitle')}>
                  {subtitle}
                </a>
              }
              onOpen={() => setOpenHoursModal(true)}
              onClose={() => setOpenHoursModal(false)}
              initialDate={date.toISO()}
              updateSelectedDate={(jsDate) => {
                if (jsDate instanceof Date) {
                  const newDate = DateTime.fromJSDate(jsDate);
                  if (newDate.isValid) {
                    setDate(newDate);
                  }
                }
              }}
              openingHoursBanner={openingHoursBanner}
              openingHours={venue?.openingHours}
              venueSlug={venue?.slug}
              venueId={venue?.id}
            />
          )
        )}
      </div>
      {Boolean(item?.fields?.links?.length) && (
        <Actions className={classNames('actions')}>
          {item.fields.links.map((link) => {
            return (
              <Link
                style={ctaStyles}
                key={link.sys.id}
                component={link}
                refs={refs}
                handleTrack={handleTrack}
              />
            );
          })}
        </Actions>
      )}
    </div>
  );
}
